import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Clock, Receipt, AlertCircle, ArrowLeftRight, Wallet, Users, Sparkles, ArrowDownCircle, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import EmptyState from '../components/EmptyState';
import { AllCaughtUp } from '../components/illustrations';
import StatusBadge from '../components/ui/status-badge';
import { formatNXC } from '../lib/formatNXC';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useRealtime } from '../context/RealtimeContext';
import { useAuth } from '../context/AuthContext';
import CommentThread from '../components/CommentThread';

export default function ApprovalsPage() {
  const [expenses, setExpenses] = useState([]);
  // iter105 — pending coin transfers also live in the Approvals inbox.
  const [transfers, setTransfers] = useState([]);
  // iter220 — pending fund redemption requests
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState('all'); // all | admin | peer | fund | mine_paid | transfers
  // iter244 — bulk selection + stale collapse
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showStale, setShowStale] = useState(false);
  const { onEvent } = useRealtime();
  const { user } = useAuth();
  const location = useLocation();

  // Deep-link: ?focus=transfer:{id} → ring-highlight + scroll to the card.
  const focusedTransferId = (() => {
    const p = new URLSearchParams(location.search || '');
    const f = p.get('focus');
    if (f && f.startsWith('transfer:')) return f.slice('transfer:'.length);
    return null;
  })();
  const focusedRef = useRef(null);
  const [focusGlow, setFocusGlow] = useState(!!focusedTransferId);

  useEffect(() => {
    fetchPendingExpenses();
    fetchPendingTransfers();
    fetchPendingRedemptions();
  }, []);

  // After data arrives, scroll the focused card into view and fade the glow.
  useEffect(() => {
    if (focusedTransferId && transfers.length > 0 && focusedRef.current) {
      focusedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const t = setTimeout(() => setFocusGlow(false), 4000);
      return () => clearTimeout(t);
    }
  }, [focusedTransferId, transfers.length]);

  // Real-time refresh on expense/transfer events
  useEffect(() => {
    return onEvent(
      [
        'expense_pending',
        'expense_approved',
        'expense_rejected',
        'expense_reversed',
        'expense_deleted',
        'coin_transfer_request',
        'coin_transfer_approved',
        'coin_transfer_rejected',
      ],
      () => { fetchPendingExpenses(); fetchPendingTransfers(); }
    );
  }, [onEvent]);

  const fetchPendingExpenses = async () => {
    try {
      const response = await api.get('/expenses/pending');
      setExpenses(response.data);
    } catch (error) {
      console.error('Failed to fetch pending expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingTransfers = async () => {
    try {
      const response = await api.get('/coin-transfers/pending');
      setTransfers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending transfers:', error);
    }
  };

  const fetchPendingRedemptions = async () => {
    try {
      const response = await api.get('/wallet/fund-redemptions/pending');
      setRedemptions(response.data?.rows || []);
    } catch (error) {
      console.error('Failed to fetch pending redemptions:', error);
    }
  };

  const handleApproveRedemption = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/wallet/fund-redemptions/${id}/approve`, {});
      toast.success('Redemption approved — money moved to their wallet');
      setRedemptions((r) => r.filter((x) => x.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectRedemption = async (id) => {
    const reason = window.prompt('Reason for rejecting? (optional)') || '';
    setProcessing(true);
    try {
      await api.post(`/wallet/fund-redemptions/${id}/reject`, { reason });
      toast.success('Redemption declined');
      setRedemptions((r) => r.filter((x) => x.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => formatNXC(amount);

  const handleApprove = async (expenseId) => {
    setProcessing(true);
    try {
      const r = await api.put(`/expenses/${expenseId}`, { status: 'approved' });
      // iter274 — member-approval vote may not fully approve yet
      if (r.data?.fully_approved === false) {
        toast.success(r.data.message || 'Approval recorded');
        const resp = await api.get('/expenses/pending');
        setExpenses(resp.data || []);
      } else {
        toast.success('Expense approved!');
        setExpenses(expenses.filter(e => e.id !== expenseId));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedExpense) return;
    
    setProcessing(true);
    try {
      await api.put(`/expenses/${selectedExpense.id}`, {
        status: 'rejected',
        rejection_reason: rejectionReason,
      });
      toast.success('Expense rejected');
      setExpenses(expenses.filter(e => e.id !== selectedExpense.id));
      setShowRejectModal(false);
      setSelectedExpense(null);
      setRejectionReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  // iter167 (Q2d) — One-tap "Reroute to Group Fund" on a pending member-paid expense.
  const handleRerouteToFund = async (expenseId) => {
    if (!window.confirm('Reroute this expense to be paid from the Group Fund? This clears the split and the named-payer attribution.')) return;
    setProcessing(true);
    try {
      await api.post(`/expenses/${expenseId}/reroute-to-fund`);
      toast.success('Rerouted to Group Fund — now awaiting approval');
      fetchPendingExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reroute');
    } finally {
      setProcessing(false);
    }
  };

  // iter167 (Q3a) — Named-payer consent actions.
  const handlePayerConsent = async (expenseId, decision) => {
    setProcessing(true);
    try {
      await api.post(`/expenses/${expenseId}/payer-consent`, { decision });
      if (decision === 'approved') {
        toast.success('Thanks — confirmed. Admin will approve next.');
      } else {
        toast.success('Marked as not yours — expense was auto-rejected.');
      }
      setExpenses(expenses.filter(e => e.id !== expenseId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update consent');
    } finally {
      setProcessing(false);
    }
  };

  // ---- Coin transfer actions ----
  const handleApproveTransfer = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/coin-transfers/${id}/approve`);
      toast.success('Transfer approved');
      setTransfers((t) => t.filter((x) => x.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectTransfer = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/coin-transfers/${id}/reject`);
      toast.success('Transfer rejected');
      setTransfers((t) => t.filter((x) => x.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  // Role helpers per transfer — only items where THIS user must act are shown.
  // The /coin-transfers/pending endpoint already scopes fund-payer requests to
  // groups where the current user is admin, so any fund-payer item here is
  // actionable by us.
  const transfersForMe = transfers.filter((t) => {
    const isRequest = t.direction === 'request';
    const isFundPayer = isRequest && t.from_user_id === 'group_fund';
    if (isRequest) {
      if (isFundPayer) return true; // backend already scoped to my admin groups
      return t.from_user_id === user?.id; // peer payer
    }
    return t.to_user_id === user?.id; // send → recipient
  });

  // iter244 — approvals older than 30 days are collapsed by default
  const STALE_MS = 30 * 24 * 60 * 60 * 1000;
  const isStale = (e) => {
    const t = new Date(e.created_at || e.date || 0).getTime();
    return Number.isFinite(t) && t > 0 && (Date.now() - t) > STALE_MS;
  };
  const staleExpenses = expenses.filter(isStale);
  const visibleExpenses = showStale ? expenses : expenses.filter((e) => !isStale(e));

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runBulk = async (status) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const verb = status === 'approved' ? 'Approve' : 'Reject';
    if (!window.confirm(`${verb} ${ids.length} expense(s)?`)) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map((id) => api.put(`/expenses/${id}`, { status }))
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    if (ok > 0) toast.success(`${ok} expense(s) ${status}`);
    if (fail > 0) toast.error(`${fail} failed — may need payer consent or peer approval first`);
    setSelected(new Set());
    setBulkBusy(false);
    fetchPendingExpenses();
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="approvals-loading">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-32 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-2xl font-bold">Pending Approvals</h1>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="w-5 h-5" />
          <span className="font-semibold">{visibleExpenses.length + transfersForMe.length + redemptions.length}</span>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-1.5 pb-2 mb-4" data-testid="approval-filters">
        {[
          { id: 'all', label: 'All', count: visibleExpenses.length + transfersForMe.length + redemptions.length },
          { id: 'consent', label: 'Confirm I Paid', count: visibleExpenses.filter((e) => e.needs_my_payer_consent).length },
          { id: 'admin', label: 'Pending Admin', count: visibleExpenses.filter((e) => e.approval_type !== 'peer' && e.paid_by !== 'group_fund' && !e.needs_my_payer_consent).length },
          { id: 'peer', label: 'Peer', count: visibleExpenses.filter((e) => e.approval_type === 'peer').length },
          { id: 'fund', label: 'From Fund', count: visibleExpenses.filter((e) => e.paid_by === 'group_fund').length },
          { id: 'mine_paid', label: 'I Paid', count: visibleExpenses.filter((e) => e.paid_by === user?.id).length },
          { id: 'transfers', label: 'Money Transfers', count: transfersForMe.length },
          { id: 'redemptions', label: 'Fund Redemptions', count: redemptions.length },
        ].filter((f) => f.id === 'all' || f.count > 0 || filter === f.id).map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border-2 transition-colors ${filter === f.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:border-primary/40'}`}
            data-testid={`filter-${f.id}`}
          >
            {f.label} <span className="opacity-70">({f.count})</span>
          </button>
        ))}
      </div>

      {/* iter220 — Pending Fund Redemption Requests */}
      {(filter === 'all' || filter === 'redemptions') && redemptions.length > 0 && (
        <div className="space-y-3 mb-5" data-testid="pending-redemptions-section">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1.5">
            <ArrowDownCircle className="w-3 h-3" />
            Fund Redemptions · {redemptions.length}
          </p>
          <AnimatePresence>
            {redemptions.map((r) => {
              const iAmRequester = r.user_id === user?.id;
              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="p-4 border-2 rounded-2xl" data-testid={`redemption-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
                            <ArrowDownCircle className="w-5 h-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold truncate">
                              {iAmRequester ? 'You' : r.user_name} want{iAmRequester ? '' : 's'} {formatCurrency(r.amount)} from {r.group_name}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              Fund → wallet redemption request
                              {r.note && ` · "${r.note}"`}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                          <span>Their credit: {formatCurrency(r.coin_balance_at_request)}</span>
                          <span>·</span>
                          <span>Fund cash: {formatCurrency(r.fund_balance_at_request)}</span>
                        </div>
                      </div>
                      {!iAmRequester && (
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectRedemption(r.id)}
                            disabled={processing}
                            data-testid={`redemption-reject-${r.id}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Decline
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveRedemption(r.id)}
                            disabled={processing}
                            data-testid={`redemption-approve-${r.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Approve
                          </Button>
                        </div>
                      )}
                      {iAmRequester && (
                        <span className="text-xs font-bold uppercase px-2 py-1 rounded-full bg-amber-500/15 text-amber-700">
                          Awaiting admin
                        </span>
                      )}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Pending Coin Transfers — only shown when filter allows */}
      {(filter === 'all' || filter === 'transfers') && transfersForMe.length > 0 && (
        <div className="space-y-3 mb-5" data-testid="pending-transfers-section">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1.5">
            <ArrowLeftRight className="w-3 h-3" />
            Money Transfers · {transfersForMe.length}
          </p>
          <AnimatePresence>
            {transfersForMe.map((t) => {
              const isRequest = t.direction === 'request';
              const isFundPayer = isRequest && t.from_user_id === 'group_fund';
              const amount = formatNXC(t.amount, t.group_currency);
              const headline = isRequest
                ? (isFundPayer
                    ? `${t.to_user_name} requests ${amount} from the group fund`
                    : `${t.to_user_name} requests ${amount} from you`)
                : `${t.from_user_name} wants to transfer ${amount} to you`;
              const isFocused = focusedTransferId === t.id;
              return (
                <motion.div
                  key={t.id}
                  ref={isFocused ? focusedRef : undefined}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card
                    className={`p-4 border-2 rounded-2xl transition-shadow ${
                      isFocused && focusGlow
                        ? 'ring-4 ring-amber-400/60 border-amber-400 shadow-lg shadow-amber-500/20'
                        : 'border-orange-500/40'
                    }`}
                    data-testid={`pending-transfer-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-700 font-semibold">
                            {t.group_name}
                          </span>
                          <span className="text-xs uppercase tracking-wider font-bold text-orange-600">
                            {isRequest ? 'Money Request' : 'Money Transfer'}
                          </span>
                        </div>
                        <h3 className="font-semibold text-base leading-tight" data-testid={`pending-transfer-headline-${t.id}`}>
                          {headline}
                        </h3>
                        {t.note && <p className="text-xs text-muted-foreground mt-1 truncate">{t.note}</p>}
                      </div>
                      <p className="font-display text-xl font-bold tabular-nums shrink-0">
                        {amount}
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleApproveTransfer(t.id)}
                        disabled={processing}
                        data-testid={`approve-transfer-${t.id}`}
                        className="flex-1 rounded-full bg-positive hover:bg-positive/90"
                      >
                        <Check className="w-4 h-4 mr-2" /> Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleRejectTransfer(t.id)}
                        disabled={processing}
                        data-testid={`reject-transfer-${t.id}`}
                        className="flex-1 rounded-full border-2 border-destructive text-destructive hover:bg-destructive hover:text-white"
                      >
                        <X className="w-4 h-4 mr-2" /> Reject
                      </Button>
                    </div>
                    <div className="mt-4 pt-4 border-t border-border">
                      <CommentThread entityType="coin_transfer" entityId={t.id} compact />
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Pending Expenses List */}
      {(() => {
        if (filter === 'transfers') {
          // Transfers-only view; no expense list. Empty-state if no transfers.
          if (transfersForMe.length === 0) {
            return (
              <EmptyState
                illustration={AllCaughtUp}
                title="No transfer requests"
                description="When someone requests or transfers ₹ to you, it lands here."
                tone="success"
                size="lg"
                testId="no-pending-transfers"
              />
            );
          }
          return null;
        }
        const filteredExpenses = visibleExpenses.filter((e) => {
          if (filter === 'all') return true;
          if (filter === 'consent') return e.needs_my_payer_consent;
          if (filter === 'admin') return e.approval_type !== 'peer' && e.paid_by !== 'group_fund' && !e.needs_my_payer_consent;
          if (filter === 'peer') return e.approval_type === 'peer';
          if (filter === 'fund') return e.paid_by === 'group_fund';
          if (filter === 'mine_paid') return e.paid_by === user?.id;
          return true;
        });
        // iter244 — stale collapse toggle (shown wherever the list renders)
        const staleToggle = staleExpenses.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowStale((v) => !v)}
            data-testid="stale-toggle"
            className="w-full mt-3 py-2.5 rounded-xl border border-dashed border-border text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            {showStale ? 'Hide approvals older than 30 days' : `${staleExpenses.length} older than 30 days hidden — show`}
          </button>
        ) : null;
        if (filteredExpenses.length === 0 && transfersForMe.length === 0) {
          return (
            <>
              <EmptyState
                illustration={AllCaughtUp}
                title="All caught up!"
                description={filter === 'all' ? 'No pending expenses to approve right now.' : 'Nothing matches this filter.'}
                tone="success"
                size="lg"
                testId="no-pending-approvals"
              />
              {staleToggle}
            </>
          );
        }
        if (filteredExpenses.length === 0) return staleToggle;
        const selectableIds = filteredExpenses.filter((e) => !e.needs_my_payer_consent).map((e) => e.id);
        const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
        return (
          <div className="space-y-4">
            {/* iter244 — bulk actions bar */}
            {selectableIds.length > 1 && (
              <div className="flex items-center justify-between gap-2 px-1" data-testid="bulk-actions-bar">
                <button
                  type="button"
                  onClick={() => setSelected(allSelected ? new Set() : new Set(selectableIds))}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="select-all-expenses"
                >
                  {allSelected
                    ? <CheckSquare className="w-4 h-4 text-primary" />
                    : <Square className="w-4 h-4" />}
                  {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
                </button>
                {selected.size > 0 && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => runBulk('approved')}
                      disabled={bulkBusy}
                      data-testid="bulk-approve-btn"
                      className="rounded-full text-xs h-8 bg-positive hover:bg-positive/90"
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve {selected.size}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runBulk('rejected')}
                      disabled={bulkBusy}
                      data-testid="bulk-reject-btn"
                      className="rounded-full text-xs h-8 border-destructive text-destructive hover:bg-destructive hover:text-white"
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Reject {selected.size}
                    </Button>
                  </div>
                )}
              </div>
            )}
            <AnimatePresence>
              {filteredExpenses.map((expense, index) => (
              <motion.div
                key={expense.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Card
                  className={`p-4 border-2 rounded-2xl ${selected.has(expense.id) ? 'border-primary/60 bg-primary/5' : ''}`}
                  data-testid={`pending-expense-${expense.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    {!expense.needs_my_payer_consent && (
                      <button
                        type="button"
                        onClick={() => toggleSelect(expense.id)}
                        className="mr-2 mt-1 shrink-0"
                        data-testid={`select-expense-${expense.id}`}
                        aria-label="Select expense"
                      >
                        {selected.has(expense.id)
                          ? <CheckSquare className="w-5 h-5 text-primary" />
                          : <Square className="w-5 h-5 text-muted-foreground" />}
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-semibold">
                          {expense.group_name}
                        </span>
                        <StatusBadge expense={expense} verbose />
                        {/* iter167 — payer-consent state badge */}
                        {expense.payer_consent_required && expense.payer_consent_status === 'pending' && !expense.needs_my_payer_consent && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 font-bold uppercase tracking-wider"
                            data-testid={`payer-consent-pending-${expense.id}`}
                          >
                            Waiting for {expense.paid_by_name}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-lg">{expense.description}</h3>
                    </div>
                    <p className="font-display text-xl font-bold amount-display">
                      {formatCurrency(expense.amount)}
                    </p>
                  </div>

                  <div className="flex items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mb-3 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Receipt className="w-4 h-4" />
                      <span>Paid by <span className="font-semibold text-foreground">{expense.paid_by_name}</span></span>
                    </div>
                    <div>•</div>
                    <div>
                      Added by <span className="font-semibold text-foreground">{expense.created_by_name}</span>
                    </div>
                  </div>

                  {/* iter167 (Q2b) — Effect summary banner: plain-English preview
                       of what approval will do, so the approver double-checks. */}
                  {(() => {
                    if (expense.needs_my_payer_consent) return null;
                    const amt = Math.round(Number(expense.amount) || 0);
                    const splitN = (expense.split_among || []).length;
                    const perPerson = splitN > 0 ? Math.round(amt / splitN) : 0;
                    let lines = [];
                    if (expense.paid_by === 'group_fund') {
                      lines = [`Group Fund will be reduced by \u20b9${amt.toLocaleString('en-IN')}.`];
                    } else if (expense.reimburse_from_fund) {
                      lines = [
                        `Group Fund will pay \u20b9${amt.toLocaleString('en-IN')} back to ${expense.paid_by_name}.`,
                        `No member-to-member splits.`,
                      ];
                    } else if (splitN > 0) {
                      lines = [
                        `${expense.paid_by_name} will be credited \u20b9${amt.toLocaleString('en-IN')}.`,
                        `${splitN} member${splitN === 1 ? '' : 's'} each owe \u20b9${perPerson.toLocaleString('en-IN')}.`,
                      ];
                    }
                    if (lines.length === 0) return null;
                    return (
                      <div
                        className="mb-3 p-3 rounded-xl bg-muted/60 border border-border text-xs leading-relaxed"
                        data-testid={`effect-summary-${expense.id}`}
                      >
                        <p className="font-bold uppercase tracking-widest text-xs text-muted-foreground mb-1 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> If you approve
                        </p>
                        {lines.map((l, i) => (
                          <p key={i}>{l}</p>
                        ))}
                      </div>
                    );
                  })()}

                  {/* iter274 — member-approval progress */}
                  {expense.approval_type === 'members' && expense.approvals_progress && (
                    <div className="mb-3 p-3 rounded-xl bg-sky-500/10 border border-sky-500/30 text-xs leading-relaxed" data-testid={`member-approval-progress-${expense.id}`}>
                      <p className="font-bold uppercase tracking-widest text-xs text-sky-700 mb-1">
                        Member approval · {expense.approvals_progress.approved}/{expense.approvals_progress.needed} confirmed
                        {expense.approvals_progress.needed < expense.approvals_progress.total_voters ? ` (of ${expense.approvals_progress.total_voters} in split)` : ''}
                      </p>
                      {expense.approvals_progress.voter_names?.length > 0 && (
                        <p className="text-muted-foreground">Approved by: {expense.approvals_progress.voter_names.join(', ')}</p>
                      )}
                      {expense.i_already_voted && (
                        <p className="text-emerald-600 font-semibold mt-0.5">✓ You've approved — waiting for others</p>
                      )}
                      {expense.can_admin_override && !expense.needs_my_member_approval && (
                        <p className="text-muted-foreground mt-0.5">As admin, approving now overrides the member vote (logged in audit).</p>
                      )}
                    </div>
                  )}

                  {/* iter167 (Q3a) — Payer-consent action row for the named payer */}
                  {expense.needs_my_payer_consent ? (
                    <div className="space-y-3">
                      <div
                        className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/40 text-sm"
                        data-testid={`payer-consent-prompt-${expense.id}`}
                      >
                        <p className="font-bold mb-0.5">Did you pay this?</p>
                        <p className="text-xs text-muted-foreground">
                          {expense.created_by_name} added an expense saying you paid {formatCurrency(expense.amount)} for &ldquo;{expense.description}&rdquo;.
                          Please confirm or reject — admin approval is blocked until you respond.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handlePayerConsent(expense.id, 'approved')}
                          disabled={processing}
                          data-testid={`payer-consent-approve-${expense.id}`}
                          className="flex-1 rounded-full bg-positive hover:bg-positive/90"
                        >
                          <Check className="w-5 h-5 mr-2" />
                          Yes, I paid
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handlePayerConsent(expense.id, 'rejected')}
                          disabled={processing}
                          data-testid={`payer-consent-reject-${expense.id}`}
                          className="flex-1 rounded-full border-2 border-destructive text-destructive hover:bg-destructive hover:text-white"
                        >
                          <X className="w-5 h-5 mr-2" />
                          No, I did not
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleApprove(expense.id)}
                          disabled={
                            processing ||
                            (expense.payer_consent_required && expense.payer_consent_status !== 'approved') ||
                            (expense.approval_type === 'members' && expense.i_already_voted && !expense.can_admin_override)
                          }
                          data-testid={`approve-btn-${expense.id}`}
                          className="flex-1 rounded-full bg-positive hover:bg-positive/90"
                          title={
                            expense.payer_consent_required && expense.payer_consent_status !== 'approved'
                              ? `Waiting for ${expense.paid_by_name} to confirm they paid`
                              : 'Approve'
                          }
                        >
                          <Check className="w-5 h-5 mr-2" />
                          {expense.approval_type === 'members'
                            ? (expense.needs_my_member_approval
                                ? 'Confirm approval'
                                : expense.can_admin_override
                                  ? 'Approve (override)'
                                  : 'Approved ✓')
                            : 'Approve'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedExpense(expense);
                            setShowRejectModal(true);
                          }}
                          disabled={processing}
                          data-testid={`reject-btn-${expense.id}`}
                          className="flex-1 rounded-full border-2 border-destructive text-destructive hover:bg-destructive hover:text-white"
                        >
                          <X className="w-5 h-5 mr-2" />
                          Reject
                        </Button>
                      </div>
                      {/* iter167 (Q2d) — Admin-only one-tap reroute to Group Fund */}
                      {expense.paid_by !== 'group_fund' && expense.approval_type !== 'peer' && (
                        <Button
                          variant="ghost"
                          onClick={() => handleRerouteToFund(expense.id)}
                          disabled={processing}
                          data-testid={`reroute-to-fund-${expense.id}`}
                          className="w-full rounded-full text-xs h-9 text-muted-foreground hover:text-foreground border border-dashed border-border"
                        >
                          <Wallet className="w-3.5 h-3.5 mr-1.5" />
                          Reroute to Group Fund instead
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Comments + Presence */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <CommentThread entityType="expense" entityId={expense.id} compact />
                  </div>
                </Card>
              </motion.div>
              ))}
            </AnimatePresence>
            {staleToggle}
          </div>
        );
      })()}

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="sm:max-w-md" data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Reject Expense
            </DialogTitle>
          </DialogHeader>
          
          {selectedExpense && (
            <div className="space-y-5 mt-4">
              <Card className="p-4 border-2 rounded-xl bg-card">
                <p className="font-semibold">{selectedExpense.description}</p>
                <p className="text-2xl font-display font-bold mt-1 amount-display">
                  {formatCurrency(selectedExpense.amount)}
                </p>
              </Card>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  Reason for Rejection (Optional)
                </Label>
                <Input
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="e.g., Duplicate entry, incorrect amount"
                  data-testid="rejection-reason-input"
                  className="h-12 border-2 rounded-xl"
                />
              </div>

              <Button
                onClick={handleReject}
                disabled={processing}
                data-testid="confirm-reject-btn"
                className="w-full h-12 rounded-full font-bold uppercase tracking-wider bg-destructive hover:bg-destructive/90"
              >
                {processing ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
